import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme } from '../theme';

const OZ_ORANGE = '#F57C1F';
const OZ_BLUE = '#29A8E0';

const SaasBillingPage = ({ API_BASE }) => {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const token = localStorage.getItem('saasToken') || '';
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE}/saas/billing/plans`).then(r => setPlans(r.data)),
      axios.get(`${API_BASE}/saas/billing/my-subscription`, { headers }).then(r => setSubscription(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const handleSubscribe = async (planKey) => {
    setActionLoading(true);
    setMsg(null);
    try {
      const res = await axios.post(`${API_BASE}/saas/billing/subscribe`, { planKey }, { headers });
      if (res.data.shortUrl) {
        window.open(res.data.shortUrl, '_blank');
        setMsg({ type: 'success', text: `Razorpay checkout opened. Amount: ₹${res.data.total} (₹${res.data.amount} + ₹${res.data.gst} GST)` });
      } else if (res.data.success === false) {
        setMsg({ type: 'error', text: res.data.message });
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || err.message });
    }
    setActionLoading(false);
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure? Subscription will end at current period.')) return;
    setActionLoading(true);
    try {
      await axios.post(`${API_BASE}/saas/billing/cancel`, {}, { headers });
      setMsg({ type: 'success', text: 'Subscription cancelled. Active till period end.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || err.message });
    }
    setActionLoading(false);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading billing...</div>;

  const isTrialActive = subscription?.status === 'trial' && subscription?.daysLeftInTrial > 0;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Current Plan Banner */}
      {subscription && (
        <div style={{
          background: `linear-gradient(135deg, ${OZ_ORANGE}10, ${OZ_BLUE}10)`,
          border: `1px solid ${OZ_ORANGE}30`, borderRadius: 12, padding: '20px 24px', marginBottom: 28,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: '0.78rem', color: theme.textSubtle, fontWeight: 600, textTransform: 'uppercase' }}>Current Plan</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: theme.navy }}>{subscription.planDetails?.name || subscription.plan || 'Trial'}</div>
            </div>
            {isTrialActive && (
              <div style={{ background: '#FFF3E0', padding: '8px 16px', borderRadius: 8, border: '1px solid #FFB74D' }}>
                <span style={{ fontWeight: 700, color: '#E65100' }}>Trial: {subscription.daysLeftInTrial} days left</span>
              </div>
            )}
            <div style={{ fontSize: '0.82rem', color: theme.textSubtle }}>
              Status: <span style={{ fontWeight: 700, color: subscription.status === 'active' ? theme.success : subscription.status === 'trial' ? OZ_ORANGE : theme.danger }}>{subscription.status}</span>
            </div>
          </div>
          {subscription.usage && (
            <div style={{ display: 'flex', gap: 20, marginTop: 16, flexWrap: 'wrap' }}>
              {[
                { label: 'Users', val: subscription.usage.userCount, max: subscription.limits?.maxUsers },
                { label: 'Patients', val: subscription.usage.patientCount, max: subscription.limits?.maxPatients },
              ].map(u => (
                <div key={u.label} style={{ fontSize: '0.8rem' }}>
                  <span style={{ color: theme.textSubtle }}>{u.label}:</span>{' '}
                  <span style={{ fontWeight: 700 }}>{u.val || 0}</span>
                  <span style={{ color: theme.textSubtle }}>/{u.max >= 999999 ? '∞' : u.max}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {msg && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 20, fontSize: '0.88rem',
          background: msg.type === 'error' ? '#FFE4E4' : '#E4FFE4',
          color: msg.type === 'error' ? '#B00020' : '#0A7B3D',
        }}>{msg.text}</div>
      )}

      {/* Plans Grid */}
      <h2 style={{ color: theme.navy, fontSize: '1.2rem', marginBottom: 20 }}>Choose a Plan</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
        {plans.map(p => {
          const isCurrent = subscription?.plan === p.key;
          const isPopular = p.key === 'pro';
          return (
            <div key={p.key} style={{
              border: `2px solid ${isPopular ? OZ_ORANGE : theme.border}`,
              borderRadius: 14, padding: '28px 24px', position: 'relative',
              background: isCurrent ? `${OZ_ORANGE}08` : '#fff',
              boxShadow: isPopular ? '0 8px 30px rgba(245,124,31,0.12)' : theme.shadow,
            }}>
              {isPopular && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  background: OZ_ORANGE, color: '#fff', fontSize: '0.72rem', fontWeight: 700,
                  padding: '4px 14px', borderRadius: 20, letterSpacing: '0.04em',
                }}>MOST POPULAR</div>
              )}
              <div style={{ fontSize: '1.15rem', fontWeight: 700, color: theme.navy }}>{p.name}</div>
              <div style={{ margin: '12px 0 4px' }}>
                <span style={{ fontSize: '2rem', fontWeight: 800, color: theme.navy }}>
                  {p.price === 0 ? 'Custom' : `₹${p.price.toLocaleString('en-IN')}`}
                </span>
                {p.price > 0 && <span style={{ fontSize: '0.85rem', color: theme.textSubtle }}>/month + GST</span>}
              </div>
              <div style={{ fontSize: '0.82rem', color: theme.textSubtle, marginBottom: 16 }}>{p.description}</div>

              <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: 14, marginBottom: 14 }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: theme.navy, marginBottom: 8 }}>Limits</div>
                <div style={{ fontSize: '0.78rem', color: theme.textSubtle, lineHeight: 1.8 }}>
                  <div>Users: <strong>{p.limits.maxUsers >= 999999 ? '∞' : p.limits.maxUsers}</strong></div>
                  <div>Patients: <strong>{p.limits.maxPatients >= 999999 ? 'Unlimited' : p.limits.maxPatients.toLocaleString()}</strong></div>
                  <div>Branches: <strong>{p.limits.maxBranches >= 999 ? '∞' : p.limits.maxBranches}</strong></div>
                  <div>WhatsApp: <strong>{p.limits.whatsappMsgs >= 999999 ? 'Unlimited' : p.limits.whatsappMsgs + '/mo'}</strong></div>
                </div>
              </div>

              <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: 14, marginBottom: 18 }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: theme.navy, marginBottom: 8 }}>Features</div>
                <div style={{ fontSize: '0.78rem', lineHeight: 1.9 }}>
                  {Object.entries(p.features).map(([k, v]) => (
                    <div key={k} style={{ color: v ? '#0A7B3D' : '#CCC' }}>
                      {v ? '✓' : '✗'} {k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => p.price > 0 ? handleSubscribe(p.key) : setMsg({ type: 'success', text: 'Contact us for Enterprise pricing' })}
                disabled={isCurrent || actionLoading}
                style={{
                  width: '100%', padding: '12px', border: 'none', borderRadius: 8, fontWeight: 700,
                  fontSize: '0.9rem', cursor: isCurrent ? 'default' : 'pointer',
                  background: isCurrent ? '#D4E6D4' : isPopular ? OZ_ORANGE : OZ_BLUE,
                  color: isCurrent ? '#2E7D32' : '#fff',
                }}
              >
                {isCurrent ? '✓ Current Plan' : p.price === 0 ? 'Contact Us' : `Upgrade to ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>

      {subscription?.razorpaySubscriptionId && (
        <div style={{ marginTop: 28, textAlign: 'center' }}>
          <button onClick={handleCancel} disabled={actionLoading} style={{
            padding: '10px 24px', background: 'none', border: `1px solid ${theme.danger}`,
            color: theme.danger, borderRadius: 8, fontSize: '0.85rem', cursor: 'pointer',
          }}>Cancel Subscription</button>
        </div>
      )}
    </div>
  );
};

export default SaasBillingPage;
